import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { api } from "../lib/api";
import ProductCard from "../components/ProductCard";

const HERO_IMG =
  "https://images.pexels.com/photos/34284465/pexels-photo-34284465.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=1200&w=1800";

const VENDOR_HERO = {
  bundles: "https://images.unsplash.com/photo-1607082348824-0a96f2a4b9da?auto=format&fit=crop&w=1200&q=80",
  electronics: "https://images.unsplash.com/photo-1611532736597-de2d4265fba3?auto=format&fit=crop&w=1200&q=80",
  dyson: "https://images.unsplash.com/photo-1522338242992-e1a54906a8da?auto=format&fit=crop&w=1200&q=80",
  meta_glasses: "https://images.unsplash.com/photo-1577803645773-f96470509666?auto=format&fit=crop&w=1200&q=80",
  accessories: "https://images.unsplash.com/photo-1511499767150-a48a237f0083?auto=format&fit=crop&w=1200&q=80",
  jewelry: "https://images.unsplash.com/photo-1599643477877-530eb83abc8e?auto=format&fit=crop&w=1200&q=80",
  shoes: "https://images.unsplash.com/photo-1542291026-7eec264c27ff?auto=format&fit=crop&w=1200&q=80",
  clothing: "https://images.unsplash.com/photo-1551028719-00167b16eac5?auto=format&fit=crop&w=1200&q=80",
  designer: "https://images.unsplash.com/photo-1539109136881-3be0616acf4b?auto=format&fit=crop&w=1200&q=80",
  cologne: "https://images.unsplash.com/photo-1541643600914-78b084683601?auto=format&fit=crop&w=1200&q=80",
};

export default function Home() {
  const [products, setProducts] = useState([]);
  const [vendors, setVendors] = useState([]);

  useEffect(() => {
    (async () => {
      const [p, v] = await Promise.all([api.get("/products"), api.get("/vendors")]);
      setProducts(p.data);
      setVendors(v.data);
    })();
  }, []);

  const featured = products.slice(0, 8);

  return (
    <div data-testid="home-page">
      <section className="relative min-h-[92vh] overflow-hidden grain">
        <img src={HERO_IMG} alt="hero" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-black/30" />
        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 min-h-[92vh] flex flex-col justify-end pb-20">
          <div className="overline mb-6 text-white/70">Reseller — Vol. 01 / 2026</div>
          <h1 className="font-serif italic text-white leading-[0.95] text-5xl sm:text-7xl md:text-8xl tracking-tighter max-w-4xl fade-up">
            Curated hype.<br />Uncompromised luxury.
          </h1>
          <p className="mt-8 text-white/70 max-w-xl text-base leading-relaxed fade-up" style={{ animationDelay: "120ms" }}>
            AirPods, Ray-Ban Meta, Dyson, deadstock sneakers, fragrance, jewelry, designer.
            Every piece $16.99 today — 40% off. DM <a href="https://tiktok.com/@ayush.resellz" target="_blank" rel="noreferrer" className="underline">@ayush.resellz</a>.
          </p>
          <div className="mt-10 flex items-center gap-4 fade-up" style={{ animationDelay: "200ms" }}>
            <Link
              to="/catalog"
              data-testid="hero-shop-button"
              className="inline-flex items-center gap-2 px-8 py-4 bg-white text-black uppercase tracking-[0.22em] text-xs font-semibold hover:bg-white/90 transition"
            >
              Shop the edit <ArrowUpRight className="w-4 h-4" />
            </Link>
            <Link
              to="/c/bundles"
              data-testid="hero-bundle-button"
              className="inline-flex items-center gap-2 px-8 py-4 border border-white/30 text-white uppercase tracking-[0.22em] text-xs font-semibold hover:bg-white/10 transition"
            >
              $50 Mega Bundle
            </Link>
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 md:px-12 py-24">
        <div className="flex items-end justify-between mb-12">
          <div>
            <div className="overline mb-3">Nine houses</div>
            <h2 className="font-serif italic text-4xl md:text-5xl">A tightly curated edit.</h2>
          </div>
          <Link to="/catalog" data-testid="bento-view-all" className="text-xs uppercase tracking-[0.22em] text-white/60 hover:text-white">View all →</Link>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 md:gap-4">
          {vendors.map((v, i) => (
            <Link
              key={v.slug}
              to={`/c/${v.slug}`}
              data-testid={`vendor-tile-${v.slug}`}
              className={`group relative overflow-hidden bg-[#121215] ${i === 0 ? "md:col-span-2 md:row-span-2 aspect-[16/10]" : "aspect-[4/5]"}`}
            >
              <img
                src={VENDOR_HERO[v.slug]}
                alt={v.name}
                className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
              <div className="absolute inset-0 p-6 md:p-8 flex flex-col justify-end">
                <div className="overline mb-2 text-white/60">{String(i + 1).padStart(2, "0")} / {String(vendors.length).padStart(2, "0")}</div>
                <div className="font-serif italic text-3xl md:text-4xl text-white">{v.name}</div>
                <div className="text-white/60 text-sm mt-2">{v.tagline}</div>
              </div>
            </Link>
          ))}
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 md:px-12 py-16">
        <div className="flex items-end justify-between mb-12">
          <div>
            <div className="overline mb-3">Just landed</div>
            <h2 className="font-serif italic text-4xl md:text-5xl">In stock now.</h2>
          </div>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-x-4 gap-y-12">
          {featured.map((p, i) => (
            <ProductCard key={p.id} product={p} idx={i} />
          ))}
        </div>
      </section>

      <section className="max-w-5xl mx-auto px-6 md:px-12 py-32 text-center">
        <p className="font-serif italic text-3xl md:text-5xl text-white/90 leading-tight">
          &ldquo;Sourced obsessively. Sold transparently. Curated like a closet I&apos;d actually wear.&rdquo;
        </p>
        <div className="overline mt-8 text-white/40">— Ayush, founder</div>
      </section>
    </div>
  );
}
